import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { eventTypesAPI, bookingPagesAPI } from "../services/schedulerAPI";
import ConferenceProviderSelector from "../components/ConferenceProviderSelector";
import { showToast } from "../components/Toast";

const CreateOneOnOneInvite = () => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    duration_minutes: 30,
    conference_provider: "google_meet",
    description: "",
    slug: "",
    show_multiple_durations: false,
    buffer_before: 0,
    buffer_after: 0,
    // Payment fields
    requires_payment: false,
    payment_amount: 0,
    payment_currency: "USD",
    payment_required_timing: "before_confirmation",
  });

  useEffect(() => {
    loadEventTypes();
  }, []);

  const loadEventTypes = async () => {
    try {
      const data = await eventTypesAPI.list();
      setEventTypes(data);
    } catch (error) {
      console.error("Failed to load event types:", error);
      showToast.error("Failed to load existing event types");
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'name':
        if (!value || value.trim() === '') {
          newErrors.name = 'Name is required';
        } else if (value.length < 3) {
          newErrors.name = 'Name must be at least 3 characters';
        } else if (value.length > 100) {
          newErrors.name = 'Name must be less than 100 characters';
        } else {
          delete newErrors.name;
        }
        break;

      case 'duration_minutes':
        if (value < 5) {
          newErrors.duration_minutes = 'Duration must be at least 5 minutes';
        } else if (value > 480) {
          newErrors.duration_minutes = 'Duration cannot exceed 8 hours (480 minutes)';
        } else {
          delete newErrors.duration_minutes;
        }
        break;

      case 'buffer_before':
      case 'buffer_after':
        if (value < 0) {
          newErrors[name] = 'Buffer time cannot be negative';
        } else if (value > 120) {
          newErrors[name] = 'Buffer time cannot exceed 120 minutes';
        } else {
          delete newErrors[name];
        }
        break;

      case 'slug':
        if (!value || value.trim() === '') {
          newErrors.slug = 'URL slug is required';
        } else if (!/^[a-z0-9-]+$/.test(value)) {
          newErrors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens';
        } else {
          delete newErrors.slug;
        }
        break;

      case 'payment_amount':
        if (formData.requires_payment) {
          if (value <= 0) {
            newErrors.payment_amount = 'Payment amount must be greater than 0';
          } else if (value > 10000) {
            newErrors.payment_amount = 'Payment amount cannot exceed $10,000';
          } else {
            delete newErrors.payment_amount;
          }
        } else {
          delete newErrors.payment_amount;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = generateSlug(name);

    setFormData({
      ...formData,
      name,
      slug
    });

    validateField('name', name);
    validateField('slug', slug);
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    validateField(field, value);
  };

  const validateAllFields = () => {
    const fields = ['name', 'duration_minutes', 'buffer_before', 'buffer_after', 'slug'];
    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSave = async () => {
    // Validate all fields
    if (!validateAllFields()) {
      showToast.error("Please fix validation errors before saving");
      return;
    }

    setIsLoading(true);

    try {
      // First, check if we need to create an event type or use existing one
      let eventTypeId;

      const existingEventType = eventTypes.find(
        et => et.duration_minutes === formData.duration_minutes && et.name === formData.name
      );

      if (existingEventType) {
        eventTypeId = existingEventType.id;
        showToast.info("Using existing event type");
      } else {
        // Create new event type with conference provider
        showToast.info("Creating event type...");
        const newEventType = await eventTypesAPI.create({
          name: formData.name,
          description: formData.description,
          duration_minutes: formData.duration_minutes,
          buffer_before_minutes: formData.buffer_before,
          buffer_after_minutes: formData.buffer_after,
          conference_provider: formData.conference_provider,
          color: "#3B82F6",
          is_active: true,
        });
        eventTypeId = newEventType.id;
        showToast.success("Event type created successfully!");
      }

      // Create booking page
      showToast.info("Creating booking page...");
      const bookingPageData = {
        slug: formData.slug,
        title: formData.name,
        description: formData.description,
        event_type_id: eventTypeId,
        custom_questions: [],
        redirect_url: "",
        is_active: true,
        // Payment fields
        requires_payment: formData.requires_payment,
        payment_amount: formData.requires_payment ? parseFloat(formData.payment_amount) : null,
        payment_currency: formData.payment_currency,
        payment_required_timing: formData.requires_payment ? formData.payment_required_timing : null,
      };

      await bookingPagesAPI.create(bookingPageData);

      showToast.success("Booking page created successfully!");

      // Navigate back to booking pages
      setTimeout(() => {
        navigate("/calendar/booking-pages");
      }, 1000);
    } catch (error) {
      console.error("Failed to create booking page:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to create invite";
      showToast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const durationOptions = [
    { value: 15, label: "15 min" },
    { value: 30, label: "30 min" },
    { value: 45, label: "45 min" },
    { value: 60, label: "60 min" },
  ];

  const isFormValid = formData.name && formData.slug && Object.keys(errors).length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-gray-900">
              Create 1-on-1 Invite
            </h1>
            <p className="text-gray-600 mt-2">Configure your smart scheduling link with auto-generated meeting links</p>
          </motion.div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="apple-btn-secondary flex items-center gap-2"
          >
            <span>👁</span>
            {showPreview ? "Hide Preview" : "Show Preview"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="apple-card p-8 space-y-6"
          >
            {/* Basic Info Section */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase mb-4">
                BASIC INFO
              </h2>

              {/* Name Field */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <span className="text-red-500">*</span>
                  <span>Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="Discovery Call"
                  className={`apple-input w-full ${errors.name ? 'border-red-500' : ''}`}
                  required
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Event Duration */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <span>🕐</span>
                  <span>Event Duration</span>
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.duration_minutes}
                  onChange={(e) => handleFieldChange('duration_minutes', parseInt(e.target.value))}
                  className="apple-input w-full"
                >
                  {durationOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.duration_minutes && (
                  <p className="text-xs text-red-500 mt-1">{errors.duration_minutes}</p>
                )}
              </div>

              {/* Show multiple duration options */}
              <div className="flex items-center gap-3 mb-6">
                <input
                  type="checkbox"
                  id="multiple_durations"
                  checked={formData.show_multiple_durations}
                  onChange={(e) =>
                    setFormData({ ...formData, show_multiple_durations: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="multiple_durations" className="text-sm text-gray-700">
                  Show multiple duration options
                </label>
              </div>
            </div>

            {/* Smart Schedule Settings */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-sm font-bold text-gray-500 uppercase mb-4">
                SMART SCHEDULE SETTINGS
              </h2>

              {/* Conference Provider */}
              <div className="mb-6">
                <ConferenceProviderSelector
                  value={formData.conference_provider}
                  onChange={(provider) => handleFieldChange('conference_provider', provider)}
                  error={errors.conference_provider}
                />
              </div>

              {/* Buffer Times */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span>⏱️</span>
                  <span>Buffer Times</span>
                  <span className="text-gray-500 font-normal text-xs ml-2">
                    (Add padding before/after meetings)
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Before (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={formData.buffer_before}
                      onChange={(e) => handleFieldChange('buffer_before', parseInt(e.target.value) || 0)}
                      className={`apple-input w-full ${errors.buffer_before ? 'border-red-500' : ''}`}
                    />
                    {errors.buffer_before && (
                      <p className="text-xs text-red-500 mt-1">{errors.buffer_before}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      After (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={formData.buffer_after}
                      onChange={(e) => handleFieldChange('buffer_after', parseInt(e.target.value) || 0)}
                      className={`apple-input w-full ${errors.buffer_after ? 'border-red-500' : ''}`}
                    />
                    {errors.buffer_after && (
                      <p className="text-xs text-red-500 mt-1">{errors.buffer_after}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Settings */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-sm font-bold text-gray-500 uppercase mb-4">
                PAYMENT SETTINGS
              </h2>

              {/* Enable Payment Toggle */}
              <div className="flex items-center justify-between mb-6 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💳</span>
                  <div>
                    <div className="font-semibold text-gray-900">Collect payment</div>
                    <div className="text-sm text-gray-600">Require payment for this booking</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_payment}
                    onChange={(e) => {
                      setFormData({ ...formData, requires_payment: e.target.checked });
                      if (!e.target.checked) {
                        // Clear payment errors when disabled
                        const newErrors = { ...errors };
                        delete newErrors.payment_amount;
                        setErrors(newErrors);
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {/* Payment Amount and Timing (shown when payment enabled) */}
              {formData.requires_payment && (
                <div className="space-y-4 animate-fadeIn">
                  {/* Payment Amount */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <span>💰</span>
                      <span>Payment Amount</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.payment_amount}
                        onChange={(e) => handleFieldChange('payment_amount', parseFloat(e.target.value) || 0)}
                        placeholder="50.00"
                        className={`apple-input w-full pl-8 ${errors.payment_amount ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {errors.payment_amount && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <span>⚠️</span>
                        {errors.payment_amount}
                      </p>
                    )}
                  </div>

                  {/* Payment Timing */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <span>⏰</span>
                      <span>Payment Timing</span>
                    </label>
                    <select
                      value={formData.payment_required_timing}
                      onChange={(e) => handleFieldChange('payment_required_timing', e.target.value)}
                      className="apple-input w-full"
                    >
                      <option value="before_confirmation">
                        Required before confirmation
                      </option>
                      <option value="optional">
                        Optional (pay what you want)
                      </option>
                    </select>
                    <p className="text-xs text-gray-500 mt-2">
                      {formData.payment_required_timing === 'before_confirmation'
                        ? 'Payment must be completed before the booking is confirmed'
                        : 'Guest can choose to pay or skip payment'}
                    </p>
                  </div>

                  {/* Payment Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600 mt-0.5">ℹ️</span>
                      <div className="text-xs text-blue-800">
                        <p className="font-semibold mb-1">Stripe Integration</p>
                        <p>
                          Secure payment links will be generated automatically via Stripe.
                          Make sure you have configured your Stripe API keys in settings.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description (Optional)
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Simple toolbar */}
                <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1">
                  <button className="p-2 hover:bg-gray-200 rounded" title="Bold">
                    <strong>B</strong>
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded" title="Italic">
                    <em>I</em>
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded" title="Underline">
                    <u>U</u>
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded" title="Link">
                    🔗
                  </button>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Add a description for your invite..."
                  rows={4}
                  className="w-full px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Booking Link Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-1">Booking Link</div>
              <div className="flex items-center gap-2">
                <code className="text-sm text-gray-700 flex-1 truncate">
                  {window.location.origin}/book/{formData.slug || "your-invite"}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/book/${formData.slug}`);
                    showToast.success("Link copied to clipboard!");
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                  disabled={!formData.slug}
                >
                  📋 Copy
                </button>
              </div>
            </div>
          </motion.div>

          {/* Preview Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="apple-card p-8 bg-white"
          >
            <div className="text-sm text-gray-500 mb-4">PREVIEW</div>

            <div className="border-2 border-gray-200 rounded-xl p-6 bg-gradient-to-br from-gray-50 to-white">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {formData.name || "Your Event Name"}
              </h3>

              <div className="flex items-center gap-2 text-gray-600 mb-4">
                <span>🕐</span>
                <span>{formData.duration_minutes} min</span>
              </div>

              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <span>{formData.conference_provider === 'zoom' ? '🎥' : '📆'}</span>
                <span className="text-sm">
                  {formData.conference_provider === 'zoom' ? 'Zoom Meeting' : 'Google Meet'}
                </span>
              </div>

              {/* Payment Badge */}
              {formData.requires_payment && formData.payment_amount > 0 && (
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mb-4">
                  <span>💰</span>
                  <span>${formData.payment_amount}</span>
                  {formData.payment_required_timing === 'optional' && (
                    <span className="text-xs opacity-75">(optional)</span>
                  )}
                </div>
              )}

              {(formData.buffer_before > 0 || formData.buffer_after > 0) && (
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                  <span>⏱️</span>
                  <span>
                    {formData.buffer_before > 0 && `${formData.buffer_before}m before`}
                    {formData.buffer_before > 0 && formData.buffer_after > 0 && ' • '}
                    {formData.buffer_after > 0 && `${formData.buffer_after}m after`}
                  </span>
                </div>
              )}

              {formData.description && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {formData.description}
                  </p>
                </div>
              )}

              <div className="mt-6">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Select a Date
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {[...Array(14)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-600"
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => navigate("/calendar/select-link-type")}
            className="apple-btn-secondary"
            disabled={isLoading}
          >
            ← Back
          </button>
          <button
            onClick={handleSave}
            disabled={!isFormValid || isLoading}
            className="apple-btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <span>Create Booking Page</span>
                <span>→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOneOnOneInvite;
